import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { FaPlus, FaEdit, FaTrash, FaTimes } from 'react-icons/fa';
import { 
  ManufacturerInterface,
  getManufacturers,
  deleteManufacturer
} from '../../api/manufacturers';
import { ManufacturerFormModal } from './ManufacturerFormModal';

interface ManufacturerManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onManufacturersChanged: () => void;
}

export const ManufacturerManager: React.FC<ManufacturerManagerProps> = ({ 
  isOpen, 
  onClose, 
  onManufacturersChanged 
}) => {
  const { keycloak } = useKeycloak();
  const [manufacturers, setManufacturers] = useState<ManufacturerInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingManufacturer, setEditingManufacturer] = useState<ManufacturerInterface | null>(null);

  const authToken = keycloak.token;

  const fetchManufacturers = async () => {
    if (!authToken) return;

    setLoading(true);
    try {
      const response = await getManufacturers(authToken, 1, 1000);
      setManufacturers(response.results);
    } catch (error) {
      console.error('Error fetching manufacturers:', error);
      toast.error('Failed to load manufacturers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchManufacturers();
    }
  }, [isOpen, authToken]);

  const handleAddNew = () => {
    console.log('🏭 Opening Add Manufacturer modal...');
    setEditingManufacturer(null);
    setShowFormModal(true);
  };

  const handleEdit = (manufacturer: ManufacturerInterface) => {
    setEditingManufacturer(manufacturer);
    setShowFormModal(true);
  };

  const handleFormSuccess = () => {
    console.log('✅ Manufacturer form success, refreshing list...');
    fetchManufacturers();
    onManufacturersChanged();
  };

  const handleDelete = async (manufacturerId: string) => {
    if (!authToken) return;

    if (window.confirm('Are you sure you want to delete this manufacturer?')) {
      try {
        await deleteManufacturer(authToken, manufacturerId);
        toast.success('Manufacturer deleted successfully');
        await fetchManufacturers();
        onManufacturersChanged();
      } catch (error) {
        console.error('Error deleting manufacturer:', error);
        toast.error('Failed to delete manufacturer');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-2xl font-bold text-gray-800">
              Manufacturer Management
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              <FaTimes />
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-700">Current Manufacturers</h3>
              <button
                onClick={handleAddNew}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center gap-2"
              >
                <FaPlus className="text-sm" />
                Add Manufacturer
              </button>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading manufacturers...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {manufacturers.map((manufacturer) => (
                  <div key={manufacturer.id} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h4 className="font-semibold text-gray-800">{manufacturer.name}</h4>
                          {manufacturer.is_active ? (
                            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                              Active
                            </span>
                          ) : (
                            <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
                              Inactive
                            </span>
                          )}
                        </div>
                        {manufacturer.description && (
                          <p className="text-gray-600 mt-1">{manufacturer.description}</p>
                        )}
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
                          {manufacturer.country && (
                            <span>Country: {manufacturer.country}</span>
                          )}
                          {manufacturer.website && (
                            <a href={manufacturer.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              Website
                            </a>
                          )}
                          {manufacturer.contact_email && (
                            <span>Email: {manufacturer.contact_email}</span>
                          )}
                          {manufacturer.contact_phone && (
                            <span>Phone: {manufacturer.contact_phone}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(manufacturer)}
                          className="p-2 text-blue-500 hover:text-blue-700"
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(manufacturer.id)}
                          className="p-2 text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {manufacturers.length === 0 && !loading && (
                  <div className="text-center py-8 text-gray-500">
                    No manufacturers added yet.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Manufacturer Form Modal */}
      <ManufacturerFormModal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        manufacturer={editingManufacturer}
        onSuccess={handleFormSuccess}
      />
    </>
  );
}; 